import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import axios from "axios";
import { useAeraWebSocket } from "@/hooks/useAeraWebSocket";
import PhoneAIIcon from "./PhoneAIIcon";
import ProcessingIndicator from "./ProcessingIndicator";
import ScrollToBottomButton from "./ScrollToBottomButton";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { ChatInput } from "./chat/ChatInput";

interface ChatMessage {
  id?: string;
  role: "user" | "ai";
  text: string;
  isStreaming?: boolean;
  isEnding?: boolean;
  apiPath?: string;
}

interface TestingChatPanelProps {
  workerName?: string;
  isHired?: boolean;
  taskId: string; // This corresponds to the ID in the URL
  onContentScrollStateChange?: (isScrolled: boolean) => void;
}

type MarkdownLinkProps = ComponentPropsWithoutRef<"a"> & { node?: unknown };
type MarkdownImgProps = ComponentPropsWithoutRef<"img"> & { node?: unknown };

type HistoryMessage = {
  role?: string;
  sender?: { type?: string };
  text?: string;
  message?: string;
  content?: string;
};

type WsMessage = {
  event?: "publish" | "stream" | string;
  data?: {
    request_id?: string;
    api_path?: string;
    text?: string;
    end?: boolean;
    responses?: Array<{ assistant_response?: string }>;
    agent_response?: string;
    response?: string;
    content?: string;
    [key: string]: unknown;
  };
};

const Icon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const icons: Record<string, React.ReactNode> = {
    hourglass: (
      <>
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
        <path d="M7 22v-4.172a2 2 0 0 1 .586-1.414L12 12 7.586 7.586A2 2 0 0 1 7 6.172V2" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[name]}
    </svg>
  );
};

const parseMessage = (content: string) => {
  // Replace escaped newlines with actual newlines
  content = content.replace(/\\n/g, "\n");
  // Replace \n\n with \n + zero-width space + \n so remark-breaks produces <br><br>
  content = content.replace(/\n\n/g, "\n\u200B\n");
  // Escape "N)" at start of lines to prevent Markdown from creating <ol><li>
  content = content.replace(/(^|\n)(\d+)\) /g, "$1$2\\) ");
  const lines = content.split("\n");
  const textLines: string[] = [];
  const attachmentLines: string[] = [];
  let hasLoadingSpinner = false;

  // Check for LOADING_SPINNER marker
  if (content.includes("[[LOADING_SPINNER]]")) {
    hasLoadingSpinner = true;
    content = content.replace(/\[\[LOADING_SPINNER\]\]/g, "").trim();
  }

  // Regex to match a line that contains ONLY markdown links [text](url)
  const linkLineRegex = /^(\s*\[[^\]]+\]\([^\)]+\)\s*)+$/;

  lines.forEach((line) => {
    if (linkLineRegex.test(line)) {
      attachmentLines.push(line);
    } else {
      textLines.push(line);
    }
  });

  return {
    text: textLines
      .join("\n")
      .replace(/(\n\u200B|\n)+$/, "")
      .trim(),
    attachments: attachmentLines.join("\n").trim(),
    hasLoadingSpinner,
  };
};

export default function TestingChatPanel({
  workerName = "AI Worker",
  taskId,
  onContentScrollStateChange,
}: TestingChatPanelProps) {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [, setHasNoHistory] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const isLoadingRef = useRef(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);
  const ownedRequestsRef = useRef<Map<string, "greeting" | "webhook">>(
    new Map(),
  );
  const lastProcessedMsgKeyRef = useRef("");
  const streamThrottleRef = useRef<Record<string, { revealed: string }>>({});
  const pendingWsByRequestIdRef = useRef<
    Map<
      string,
      {
        streamText?: string;
        isEnd?: boolean;
        publishText?: string;
        apiPath?: string;
      }
    >
  >(new Map());
  const baseUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${taskId}`;

  const markdownComponents = useMemo<Components>(
    () => ({
      h1: "h1",
      h2: "h2",
      h3: "h3",
      h4: "h4",
      h5: "h5",
      h6: "h6",
      a: (rawProps) => {
        const props = rawProps as MarkdownLinkProps;
        return (
          <AeraButton
            as="a"
            {...props}
            variant="link"
            textColor="amberdark"
            target="_blank"
            rel="noopener noreferrer"
          />
        );
      },
      img: (rawProps) => {
        const props = rawProps as MarkdownImgProps;
        if (props.alt === "cursor") {
          return (
            <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "0ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "150ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "300ms", animationDuration: "1s" }}
              ></span>
            </span>
          );
        }
        return (
          <AeraImage
            native
            src={typeof props.src === "string" ? props.src : ""}
            alt={props.alt ?? ""}
            className="max-w-full rounded-lg"
          />
        );
      },
    }),
    [],
  );

  // Helper to get current user ID
  const getUserId = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user.userId || "demo";
      }
    } catch {}
    return "demo";
  };

  const currentUserId = getUserId();

  const flushPendingForRequestId = useCallback((requestId: string) => {
    const pending = pendingWsByRequestIdRef.current.get(requestId);
    if (!pending) return;
    pendingWsByRequestIdRef.current.delete(requestId);

    if (typeof pending.streamText === "string") {
      setChatHistory((prev) => {
        if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
        const idx = prev.findIndex(
          (m) => m.id === requestId && m.role === "ai",
        );
        if (idx !== -1) {
          return [
            ...prev.slice(0, idx),
            {
              ...prev[idx],
              text: pending.streamText ?? "",
              isStreaming: true,
              isEnding: !!pending.isEnd,
            },
            ...prev.slice(idx + 1),
          ];
        }
        return [
          ...prev,
          {
            role: "ai" as const,
            text: pending.streamText ?? "",
            id: requestId,
            isStreaming: true,
            isEnding: !!pending.isEnd,
          },
        ];
      });
    }

    if (pending.publishText) {
      setChatHistory((prev) => {
        const filtered = prev.filter(
          (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
        );
        if (
          filtered.some(
            (m) => m.text === pending.publishText && m.role === "ai",
          )
        )
          return filtered;
        return [
          ...filtered,
          {
            role: "ai" as const,
            text: pending.publishText!,
            id: `${requestId}-publish`,
            isStreaming: false,
            apiPath: pending.apiPath,
          },
        ];
      });
      setIsTyping(false);
    }
  }, []);

  // Load chat history
  useEffect(() => {
    async function loadChatHistory() {
      if (!taskId) {
        setIsLoadingHistory(false);
        return;
      }

      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoadingHistory(true);

      const fetchGreeting = async () => {
        try {
          const greetingUrl = `${baseUrl}/v1/greeting`;
          const token =
            typeof window !== "undefined"
              ? localStorage.getItem("auth_token")
              : null;
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          if (token) {
            headers["Authorization"] =
              `Bearer ${token.trim().startsWith("Bearer ") ? token.trim().substring(7) : token.trim()}`;
          }

          const response = await axios.post(
            "/api/proxy",
            {
              url: greetingUrl,
              method: "POST",
              headers: headers,
              data: {
                session_id: `demo-${taskId}`,
                user_id: currentUserId,
                product_id: taskId,
                language: "ไทย",
                suggested_actions: false,
              },
            },
            { timeout: 120000 },
          );

          const data = response.data;
          const greetingText =
            data.response || data.message || data.text || data.greeting;
          // Register request_id if available
          if (data.request_id) {
            ownedRequestsRef.current.set(data.request_id, "greeting");
            flushPendingForRequestId(data.request_id);
          }

          // If the response is just a queue acknowledgment, don't show it. The websocket will stream the real message.
          if (
            greetingText &&
            !greetingText.toLowerCase().includes("queued for processing")
          ) {
            setChatHistory([{ role: "ai", text: greetingText }]);
            setHasNoHistory(false);
            setIsTyping(false);
          } else if (
            greetingText?.toLowerCase().includes("queued for processing")
          ) {
            // Empty out history so the stream can start fresh
            setChatHistory([]);
            setHasNoHistory(false);
            setIsTyping(true); // Keep typing indicator on since stream is coming
          } else {
            setChatHistory([]);
            setHasNoHistory(true);
            setIsTyping(false);
          }
        } catch (error) {
          console.error("Error fetching greeting:", error);
          setChatHistory([]);
          setHasNoHistory(true);
          setIsTyping(false);
        }
      };

      try {
        const historyUrl = `${baseUrl}/v1/chat/history?platform=line&uid=${currentUserId}&limit=50`;

        // Get token from localStorage
        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("auth_token")
            : null;
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        if (token) {
          headers["Authorization"] =
            `Bearer ${token.trim().startsWith("Bearer ") ? token.trim().substring(7) : token.trim()}`;
        }

        // Using axios with proxy API and 120 second timeout
        const response = await axios.post(
          "/api/proxy",
          {
            url: historyUrl,
            method: "GET",
            headers: headers,
          },
          {
            timeout: 120000, // 120 seconds
          },
        );

        const data = response.data;
        let messages = [];
        if (Array.isArray(data)) messages = data;
        else if (data.messages && Array.isArray(data.messages))
          messages = data.messages;
        else if (data.data && Array.isArray(data.data)) messages = data.data;

        const loadedHistory: ChatMessage[] = (messages as HistoryMessage[]).map(
          (msg) => ({
            role:
              msg.role === "user" || msg.sender?.type === "user"
                ? "user"
                : "ai",
            text: msg.text || msg.message || msg.content || "",
          }),
        );

        if (loadedHistory.length > 0) {
          setChatHistory(loadedHistory.reverse());
          setHasNoHistory(false);
        } else {
          await fetchGreeting();
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        await fetchGreeting();
      } finally {
        setIsLoadingHistory(false);
        isLoadingRef.current = false;
      }
    }

    loadChatHistory();
  }, [taskId, baseUrl, currentUserId, flushPendingForRequestId]);

  const handleScroll = useCallback(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    onContentScrollStateChange?.(container.scrollTop > 0);
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 150);
  }, [onContentScrollStateChange]);

  const scrollToBottom = useCallback(() => {
    const container = chatScrollRef.current;
    if (container)
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, []);

  // Auto-scroll: always jump to bottom on initial load; smart 150px threshold after
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!chatHistory.length) return;
      const container = chatScrollRef.current;
      if (!container) return;
      if (!hasInitialScrolled.current) {
        container.scrollTop = container.scrollHeight;
        hasInitialScrolled.current = true;
        return;
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom <= 150) {
        container.scrollTop = container.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [chatHistory, isTyping]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text) return;

    setIsTyping(true);

    // Optimistically add user message
    const newHistory: ChatMessage[] = [...chatHistory, { role: "user", text }];
    setChatHistory(newHistory);
    requestAnimationFrame(() => scrollToBottom());
    setInputValue("");
    setHasNoHistory(false);

    try {
      const webhookUrl = `${baseUrl}/v1/webhooks/line`;
      const payload = {
        destination: "",
        events: [
          {
            type: "message",
            message: {
              type: "text",
              id: "", // generated?
              quotedMessageId: "",
              quoteToken: "",
              text: text,
            },
            webhookEventId: "",
            deliveryContext: { isRedelivery: false },
            timestamp: Date.now(),
            source: {
              type: "user",
              groupId: "",
              roomId: "",
              userId: currentUserId,
            },
            replyToken: "",
            mode: "active",
          },
        ],
      };

      // Get token from localStorage
      const token =
        typeof window !== "undefined"
          ? localStorage.getItem("auth_token")
          : null;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Origin: baseUrl,
      };

      if (token) {
        headers["Authorization"] =
          `Bearer ${token.trim().startsWith("Bearer ") ? token.trim().substring(7) : token.trim()}`;
      }

      // Using axios with proxy API and 120 second timeout
      await axios
        .post(
          "/api/proxy",
          {
            url: webhookUrl,
            method: "POST",
            headers: headers,
            data: payload,
          },
          {
            timeout: 120000,
          },
        )
        .then((res) => {
          // Register request_id if webhook returns one
          if (res.data?.request_id) {
            ownedRequestsRef.current.set(res.data.request_id, "webhook");
            flushPendingForRequestId(res.data.request_id);
          }
        });

      // Simulating a delay for AI to reply then re-fetching history
      // Simulating a delay for AI to reply
      // No polling needed, WebSocket will handle the response
      setTimeout(() => {
        // Keep typing indicator on for a bit if needed, or let WS handle it
      }, 2000);
    } catch (error) {
      console.error("Error in send flow:", error);
      setChatHistory((prev) => [
        ...prev,
        {
          role: "ai",
          text: "Error sending message. Please try again.",
        },
      ]);
      setIsTyping(false); // error path: no WS coming, clear immediately
    } finally {
    }
  };

  // WebSocket Integration
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
  const roomId = `user:${currentUserId}`; // Use real user ID

  const {} = useAeraWebSocket({
    roomId,
    token,
    enabled: true,
    onMessage: (msg: unknown) => {
      if (!msg) return;
      const data = msg as WsMessage;

      // Dedup: skip exact duplicate
      const msgKey = `${data.event}:${data.data?.request_id}:${data.data?.text}:${data.data?.end}`;
      if (msgKey === lastProcessedMsgKeyRef.current) return;
      lastProcessedMsgKeyRef.current = msgKey;

      if (data.event === "publish" && data.data) {
        const rawPubText =
          data.data.responses?.[0]?.assistant_response ??
          data.data.agent_response ??
          data.data.text ??
          data.data.response ??
          data.data.content;

        const pubText =
          typeof rawPubText === "string"
            ? rawPubText
            : rawPubText != null
              ? JSON.stringify(rawPubText)
              : "";
        const requestId = data.data.request_id;
        const apiPath = data.data.api_path;

        // Queue if request_id not yet registered; will be flushed after HTTP response
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(pendingWsByRequestIdRef.current.get(requestId) || {}),
            publishText:
              typeof pubText === "string"
                ? pubText
                : JSON.stringify(pubText ?? ""),
            apiPath,
          });
          return;
        }

        if (pubText.trim()) {
          setChatHistory((prev) => {
            const filtered = prev.filter(
              (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
            );
            return [
              ...filtered,
              {
                role: "ai",
                text: pubText,
                id: `${requestId}-publish`,
                isStreaming: false,
                apiPath,
              },
            ];
          });
        }
        setIsTyping(false);
        ownedRequestsRef.current.delete(requestId);
      } else if (data.event === "stream" && data.data) {
        const socketText = data.data.text ?? "";
        const requestId = data.data.request_id;
        const apiPath = data.data.api_path;
        const isEnd = data.data.end === true;

        // Queue if request_id not yet registered; will be flushed after HTTP response
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          const prevPending = pendingWsByRequestIdRef.current.get(requestId);
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(prevPending || {}),
            streamText: (prevPending?.streamText ?? "") + socketText,
            isEnd,
            apiPath,
          });
          return;
        }

        if (!streamThrottleRef.current[requestId]) {
          streamThrottleRef.current[requestId] = { revealed: "" };
        }
        const tState = streamThrottleRef.current[requestId];

        // Match AgentChatPanel behavior: even if socketText is "", still mark streaming so UI indicators render.
        tState.revealed = tState.revealed + socketText;
        const displayText = tState.revealed;
        setChatHistory((prev) => {
          if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
          const idx = prev.findIndex(
            (m) => m.id === requestId && m.role === "ai",
          );
          if (idx !== -1) {
            return [
              ...prev.slice(0, idx),
              {
                ...prev[idx],
                text: displayText,
                isStreaming: true,
                isEnding: isEnd,
              },
              ...prev.slice(idx + 1),
            ];
          }
          return [
            ...prev,
            {
              role: "ai" as const,
              text: displayText,
              id: requestId,
              isStreaming: true,
              isEnding: isEnd,
              apiPath,
            },
          ];
        });
        setIsTyping(false);

        if (isEnd) {
          delete streamThrottleRef.current[requestId];
          setIsTyping(false);

          // On end we now rely on the subsequent 'publish' event to finalize the message.
        }
      }
    },
  });

  // Removed pollForNewMessages as it is replaced by WebSocket

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Demo Header */}
        <div className="bg-aera-offwhite border-b border-aera-lightgray/80 text-aera-charcoal px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-aera-white/30 p-1 rounded">
              <PhoneAIIcon className="stroke-aera-charcoal" />
            </div>
            <span className="font-semibold text-sm">
              Demo Version (ตัวอย่าง)
            </span>
          </div>
        </div>

        <div className="flex-1 relative overflow-hidden">
          <div
            ref={chatScrollRef}
            className="absolute inset-0 p-4 overflow-y-auto space-y-4"
            onScroll={handleScroll}
          >
            <>
              {chatHistory.map((msg, i) => {
                const { text, attachments, hasLoadingSpinner } = parseMessage(
                  msg.text,
                );
                const isUser = msg.role === "user";

                return (
                  <div key={i}>
                    <div
                      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 duration-300`}
                    >
                      <div
                        className={`flex flex-col max-w-[80%] ${isUser ? "items-end" : "items-start"} ${!isUser && msg.isEnding ? "animate-pulse" : ""}`}
                      >
                        <div
                          className={`px-3 opacity-90 transition-opacity rounded-lg text-sm ${
                            isUser
                              ? "bg-aera-lightgray text-aera-charcoal rounded-br-none py-1"
                              : "bg-aera-white text-aera-charcoal border border-aera-lightgray/80 rounded-bl-none py-3"
                          }`}
                          style={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.role === "ai" && (
                            <div className="text-xs font-semibold text-aera-charcoal mb-1">
                              {workerName}
                            </div>
                          )}
                          {(text || msg.isStreaming) && (
                            <div
                              className={`prose prose-sm max-w-none ${isUser ? "prose-invert text-aera-charcoal prose-p:my-0 prose-pre:my-0 prose-a:text-aera-amberdark" : "prose-slate prose-headings:text-aera-charcoal prose-headings:font-bold prose-headings:mb-1 prose-headings:mt-2 prose-h2:text-lg prose-h3:text-base prose-h4:text-sm prose-p:leading-relaxed prose-p:my-1 prose-p:mb-2 prose-strong:text-aera-charcoal prose-strong:font-bold prose-ul:list-disc prose-ul:pl-4 prose-ul:my-1 prose-ul:space-y-1 prose-ol:list-decimal prose-ol:pl-4 prose-ol:my-1 prose-ol:space-y-1 prose-li:my-0.5 prose-code:bg-aera-white prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-aera-charcoal prose-code:text-[13px] prose-code:before:content-none prose-code:after:content-none prose-pre:bg-aera-charcoal prose-pre:text-aera-white prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto prose-a:text-aera-amberdark prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-aera-amber prose-blockquote:pl-4 prose-blockquote:italic prose-hr:my-4 prose-hr:border-gray-200 wrap-break-word"}`}
                            >
                              <div
                                className={`${hasLoadingSpinner ? "flex" : "grid"} items-center gap-2`}
                              >
                                {hasLoadingSpinner && (
                                  <div className="flex">
                                    <div
                                      className="text-aera-amber animate-spin"
                                      style={{ animationDuration: "2s" }}
                                    >
                                      <Icon name="hourglass" size={18} />
                                    </div>
                                  </div>
                                )}
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={markdownComponents}
                                >
                                  {msg.isStreaming
                                    ? `${text} ![cursor](cursor)`
                                    : text}
                                </ReactMarkdown>
                                {hasLoadingSpinner && (
                                  <div className="flex items-center gap-1 w-max">
                                    <div
                                      className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                      style={{
                                        animationDelay: "0ms",
                                        animationDuration: "1s",
                                      }}
                                    ></div>
                                    <div
                                      className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                      style={{
                                        animationDelay: "150ms",
                                        animationDuration: "1s",
                                      }}
                                    ></div>
                                    <div
                                      className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                      style={{
                                        animationDelay: "300ms",
                                        animationDuration: "1s",
                                      }}
                                    ></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {attachments && (
                            <div
                              className={`mt-2 flex flex-col ${isUser ? "items-end text-right" : "items-start text-left"} w-full`}
                            >
                              {/* <div className={`${isUser ? "text-right" : "text-left"}`}> */}
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkBreaks]}
                                components={{
                                  a: (rawProps) => {
                                    const props = rawProps as MarkdownLinkProps;
                                    const content = String(props.children);
                                    const href = props.href || "";
                                    const isFile =
                                      href.match(
                                        /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|jpg|jpeg|png|gif|webp)$/i,
                                      ) || href.includes("/uploads/documents/");

                                    if (isFile) {
                                      const fileName = content;
                                      let cleanHref = href;
                                      try {
                                        cleanHref = decodeURIComponent(href);
                                      } catch {
                                        // ignore error
                                      }
                                      const fileType =
                                        cleanHref
                                          .split("?")[0]
                                          .split(".")
                                          .pop()
                                          ?.toUpperCase() || "FILE";
                                      const isImage = [
                                        "JPG",
                                        "JPEG",
                                        "PNG",
                                        "GIF",
                                        "WEBP",
                                        "SVG",
                                        "BMP",
                                      ].includes(fileType);

                                      if (isImage) {
                                        return (
                                          <AeraImage
                                            native
                                            src={href}
                                            alt={fileName}
                                            className="max-w-full rounded-lg my-2 cursor-pointer hover:opacity-90 transition-opacity border border-gray-200"
                                            style={{ maxHeight: "300px" }}
                                          />
                                        );
                                      }

                                      return (
                                        <div
                                          className="flex items-center gap-3 p-3 bg-aera-white border border-gray-200 rounded-xl my-2 cursor-pointer hover:bg-gray-50 transition-colors max-w-sm group shadow-sm text-left"
                                          onClick={() =>
                                            window.open(href, "_blank")
                                          }
                                        >
                                          <div className="bg-aera-redlight p-2 rounded-lg text-aera-red">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                                              <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium text-aera-charcoal truncate text-sm">
                                              {fileName}
                                            </div>
                                            <div className="text-xs text-aera-charcoal">
                                              {fileType}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <AeraButton
                                        as="a"
                                        {...props}
                                        variant="link"
                                        textColor="darkamber"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {content}
                                      </AeraButton>
                                    );
                                  },
                                }}
                              >
                                {attachments}
                              </ReactMarkdown>
                              {/* </div> */}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {(isTyping || isLoadingHistory) && (
                <ProcessingIndicator
                  workerName={workerName}
                  processingMessage={
                    isLoadingHistory ? "กำลังโหลดประวัติแชท" : undefined
                  }
                />
              )}
            </>

            <div ref={chatEndRef} />
          </div>
          {/* Scroll to bottom button */}
          {showScrollButton && (
            <ScrollToBottomButton onClick={scrollToBottom} />
          )}
        </div>

        {/* Input Area */}
        <div className="p-4">
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={() => handleSendMessage()}
            placeholder={`ส่งข้อความให้ ${workerName}...`}
            disabled={isTyping}
            sendDisabled={!inputValue.trim() || isTyping}
          />
        </div>
      </div>
    </>
  );
}
